import React, { useEffect, useState, useMemo , useCallback} from "react";
import { useNavigate } from "react-router-dom";
import { useMiniERP } from "./hooks/useMiniERP";
import {
  useStatusBanner,
  StatusBanner,
} from "./components/MiniERPStatusBanner";
import { generateMemoPDF, getMemoPdfFile } from "./components/MiniERPPdf";
import { generateRegisterCSV } from "./components/MiniERPCsv";
import { usePDActiveSubscription } from "../purpledoor/hooks/pdActiveSubscription";
import { useMiniERPEDI } from "./hooks/useMiniERPEDI";
import MiniERPContactPicker from "./components/MiniERPContactPicker";
import {
  isVoiceSupported,
  useVoiceMemoAssistant,
  useSingleFieldDictation,
  VOICE_FIELD_SEQUENCE,
} from "./components/MiniERPVoice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faCalendarCheck,
  faChartBar,
  faFileAlt,
  faUsers,
  faWarehouse,
  faMoneyBill,
  faFileExport,
  faListUl,
  faTimes,
  faFilePdf,
  faShareAlt,
  faEdit,
  faTrash,
  faCheck,
  faMicrophone,
  faMicrophoneSlash,
  faVolumeUp,
  faVolumeMute,
  faFileInvoice,
  faBriefcase,
  faUserFriends,
  faExchangeAlt,
  faBrain,
  faUserClock
} from "@fortawesome/free-solid-svg-icons";
import { faBattleNet } from "@fortawesome/free-brands-svg-icons";

// Tailwind purges classes assembled at runtime via string interpolation
// (`text-${color}-400`), so every colour used below must appear literally.
const ICON_COLORS = {
  cyan: "text-cyan-400",
  emerald: "text-emerald-400",
  amber: "text-amber-400",
  rose: "text-rose-400",
  violet: "text-violet-400",
  sky: "text-sky-400",
  indigo: "text-indigo-400",
};

// ddmmyyyy/seq — seq is this memo's position within that day's entries,
// computed purely from what's already loaded. No backend field, so it can
// never drift out of sync and needs no schema change.
const buildMemoNumber = (transaction, seq) => {
  const d = new Date(transaction.date);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}${mm}${yyyy}/${String(seq).padStart(3, "0")}`;
};

const MiniERPHome = ({ authState }) => {
  const {
    fetchLedgers,
    fetchInventory,
    fetchPettyTransactions,
    createPettyTransaction,
    updatePettyTransaction,
    deletePettyTransaction,
    seedDefaultLedgers,
    userCode,
    userName,
  } = useMiniERP();

  const navigate = useNavigate();
  const { createEDITransaction } = useMiniERPEDI();
  const { isActive: isSubscribed, loadingSubs: loadingSub } = usePDActiveSubscription('MiniERPEDI');
  const { status, showSuccess, showError } = useStatusBanner();
  const [showTodayList, setShowTodayList] = useState(false);
  const [showCounterHistory, setShowCounterHistory] = useState(false);
  const [ledgerCount, setLedgerCount] = useState(0);
  const [inventoryCount, setInventoryCount] = useState(0);
  const [pettyTransactions, setPettyTransactions] = useState([]);
  const [showSeedPrompt, setShowSeedPrompt] = useState(false);
  const [loading, setLoading] = useState(true);

  // Customer (default, single-sided, unchanged) vs Business (cross-account,
  // routes to Local EDI instead of the regular counter memo)
  const [counterpartyKind, setCounterpartyKind] = useState("Customer");
  const [businessForm, setBusinessForm] = useState({
    counterpartyCode: "", itemGiven: "", itemReceived: "",
  });

  const [editingPetty, setEditingPetty] = useState(null); // transaction being edited, or null
  const [confirmDeleteId, setConfirmDeleteId] = useState(null); // two-tap delete, no native confirm() dialogs

  const [pettyForm, setPettyForm] = useState({
    type: "Sales",
    itemName: "",
    amount: "",
    paymentType: "Cash",
    customerName: "",
    customerMobile: "",
    remarks: "",
    qty: "",
    unit: "",
    isPaid: true,
  });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [ledgerRes, inventoryRes, pettyRes] = await Promise.all([
          fetchLedgers(),
          fetchInventory(),
          fetchPettyTransactions(),
        ]);

        setLedgerCount(ledgerRes?.data?.length || 0);
        setInventoryCount(inventoryRes?.data?.length || 0);
        setPettyTransactions(pettyRes?.data || []);
        setShowSeedPrompt((ledgerRes?.data?.length || 0) === 0);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSeedLedgers = async () => {
    try {
      await seedDefaultLedgers();
      const res = await fetchLedgers();
      setLedgerCount(res?.data?.length || 0);
      setShowSeedPrompt(false);
      showSuccess("Default Ledger Groups seeded successfully!");
    } catch (err) {
      showError("Failed to seed: " + err.message);
    }
  };

  const submitPettyForm = useCallback(async (viaVoice = false) => {
      if (!isSubscribed) {
        showError('An active MiniERP EDI subscription is needed to add or edit entries. You can still view and download your existing ones.');
        return;
      }
      // ============ BUSINESS PATH — routes to Local EDI, not the counter memo ============
      if (counterpartyKind === "Business") {
        if (!businessForm.counterpartyCode) {
          showError("Enter the business's MiniERP code.");
          return;
        }
        if (pettyForm.type === "Barter") {
          if (!businessForm.itemGiven || !businessForm.itemReceived) {
            showError("For a barter, fill in both what you give and what you receive.");
            return;
          }
        } else if (!pettyForm.itemName || !pettyForm.amount) {
          showError("Item name and amount are required.");
          return;
        }

        try {
          await createEDITransaction({
            transactionType: pettyForm.type,
            counterpartyCode: businessForm.counterpartyCode,
            itemName: pettyForm.itemName,
            qty: pettyForm.qty ? Number(pettyForm.qty) : undefined,
            unit: pettyForm.qty ? pettyForm.unit : undefined,
            amount: pettyForm.amount ? Number(pettyForm.amount) : 0,
            itemGiven: businessForm.itemGiven,
            itemReceived: businessForm.itemReceived,
            isPaid: pettyForm.isPaid,
            remarks: pettyForm.remarks,
          });
          showSuccess("Sent to Local EDI — waiting on both sides to confirm. View status on the Local EDI page.");
          setBusinessForm({ counterpartyCode: "", itemGiven: "", itemReceived: "" });
          setPettyForm({
            type: "Sales", itemName: "", amount: "", paymentType: "Cash",
            customerName: "", customerMobile: "", remarks: "", qty: "", unit: "", isPaid: true,
          });
        } catch (err) {
          showError(err.message);
        }
        return;
      }

      // ============ CUSTOMER PATH — unchanged from before ============
      if (!pettyForm.itemName || !pettyForm.amount) {
        showError(viaVoice ? "I didn't catch the item or amount — try again with typing or voice." : 'Item name and amount are required.');
        return;
      }
  
      const mobileDigits = pettyForm.customerMobile.replace(/\D/g, '');
      if (mobileDigits && (mobileDigits.length < 7 || mobileDigits.length > 13)) {
        showError('Mobile number looks incomplete — please check it or leave it blank.');
        return;
      }
  
      const payload = {
        ...pettyForm,
        amount: Number(pettyForm.amount),
        qty: pettyForm.qty ? Number(pettyForm.qty) : undefined,
        unit: pettyForm.qty ? pettyForm.unit : undefined,
      };
  
      try {
        if (editingPetty) {
          await updatePettyTransaction(editingPetty._id, payload);
          showSuccess(viaVoice ? '🎤 Memo updated by voice!' : 'Counter memo updated!');
        } else {
          await createPettyTransaction({ ...payload, date: new Date() });
          showSuccess(viaVoice ? '🎉 Saved via Thinky voice memo!' : 'Counter transaction recorded!');
        }
  
        const res = await fetchPettyTransactions();
        setPettyTransactions(res?.data || []);
  
        setEditingPetty(null);
        setPettyForm({
          type: 'Sales', itemName: '', amount: '', paymentType: 'Cash',
          customerName: '', customerMobile: '', remarks: '', qty: '', unit: '', isPaid: true,
        });
      } catch (err) {
        showError(err.message);
      }
    }, [pettyForm, businessForm, counterpartyKind, editingPetty, updatePettyTransaction, createPettyTransaction, createEDITransaction, fetchPettyTransactions, showSuccess, showError]);
  
    const handlePettySubmit = (e) => {
      e.preventDefault();
      submitPettyForm(false);
    };
  

  // ================= VOICE ASSISTANT =================
  const voice = useVoiceMemoAssistant({
    onWake: () => {
      // Fresh slate for a guided voice memo, so old typed values don't linger
      setPettyForm({
        type: "Sales",
        itemName: "",
        amount: "",
        paymentType: "Cash",
        customerName: "",
        customerMobile: "",
        remarks: "",
        qty: "",
        unit: "",
        isPaid: true,
      });
    },
    onFieldUpdate: (key, value) =>
      setPettyForm((f) => ({ ...f, [key]: value })),
    onSave: () => submitPettyForm(true),
    onCancel: () => {}, // fields already filled stay put — user can still submit manually
  });

  const fieldMic = useSingleFieldDictation();
  const dictateField = (fieldKey) => {
    fieldMic.startListening((text) => {
      const spec = VOICE_FIELD_SEQUENCE.find((f) => f.key === fieldKey);
      const value = spec ? spec.parse(text) : text;
      setPettyForm((f) => ({ ...f, [fieldKey]: value }));
    });
  };

  const MicButton = ({ fieldKey, title }) => {
    if (!isVoiceSupported) return null;
    return (
      <button
        type="button"
        onClick={() => dictateField(fieldKey)}
        className={`w-fit px-1.5 py-1.5 rounded-lg text-xs transition-colors ${
          fieldMic.listening
            ? "bg-rose-600 text-white animate-pulse"
            : "bg-gray-600 text-gray-300 hover:bg-cyan-600 hover:text-white"
        }`}
        title={title || "Speak this field"}
      >
        <FontAwesomeIcon icon={faMicrophone} />
      </button>
    );
  };

  const TransactionRow = ({ t }) => (
    <div className="flex justify-between items-center bg-gray-800 rounded-xl px-3 py-2 text-xs">
      <div>
        <div className="text-gray-600 font-mono text-[10px]">
          {buildMemoNumber(t, seqById.get(t._id))}
        </div>
        <div className="text-white font-medium">
          {t.itemName}
          {t.qty ? ` × ${t.qty}${t.unit ? " " + t.unit : ""}` : ""}
        </div>
        <div className="text-gray-500">
          {t.customerName || "Walk-in"}
          {t.customerMobile ? ` • ${t.customerMobile}` : ""}
          <span className="text-gray-600"> • {new Date(t.date).toLocaleDateString()}</span>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right">
          <div className={`font-semibold ${t.type === "Sales" ? "text-emerald-400" : "text-rose-400"}`}>
            ₹{t.amount}
          </div>
          <div className="text-gray-500">
            {t.paymentType}
            {t.isPaid === false ? " • Unpaid" : ""}
          </div>
        </div>
        <div className="flex gap-1">
          <button type="button" onClick={() => openEditPetty(t)} className="w-fit px-1 py-1 text-sm text-gray-400 hover:text-amber-400" title="Edit">
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button
            type="button"
            onClick={() => handleDeletePetty(t._id)}
            className={`w-fit px-1 py-1 text-sm ${confirmDeleteId === t._id ? "text-rose-400" : "text-gray-400 hover:text-rose-400"}`}
            title={confirmDeleteId === t._id ? "Tap again to confirm delete" : "Delete"}
          >
            <FontAwesomeIcon icon={confirmDeleteId === t._id ? faCheck : faTrash} />
          </button>
          <button type="button" onClick={() => handleDownloadMemo(t, seqById.get(t._id))} className="w-fit px-1 py-1 text-sm text-gray-400 hover:text-cyan-400" title="Download PDF">
            <FontAwesomeIcon icon={faFilePdf} />
          </button>
          <button type="button" onClick={() => handleShareMemo(t, seqById.get(t._id))} className="w-fit px-1 py-1 text-sm text-gray-400 hover:text-emerald-400" title="Share via WhatsApp">
            <FontAwesomeIcon icon={faShareAlt} />
          </button>
        </div>
      </div>
    </div>
  );

  const handleDownloadMemo = (transaction, seq) => {
    try {
      generateMemoPDF(transaction, buildMemoNumber(transaction, seq));
    } catch (err) {
      showError("Could not generate PDF: " + err.message);
    }
  };

  const handleShareMemo = async (transaction, seq) => {
    const memoNumber = buildMemoNumber(transaction, seq);
    const summaryText =
      `${transaction.type} Memo ${memoNumber}\n${transaction.itemName}` +
      `${transaction.qty ? ` x${transaction.qty}${transaction.unit ? " " + transaction.unit : ""}` : ""}\n` +
      `Amount: Rs.${transaction.amount}\nPayment: ${transaction.paymentType}` +
      `${transaction.customerName ? `\n${transaction.type === "Purchase" ? "Supplier" : "Customer"}: ${transaction.customerName}` : ""}` +
      `${transaction.customerMobile ? `\nMobile: ${transaction.customerMobile}` : ""}`;

    try {
      const file = getMemoPdfFile(transaction, memoNumber);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Memo ${memoNumber}`,
          text: summaryText,
        });
        return;
      }
    } catch (err) {
      if (err?.name === "AbortError") return; // user closed the native share sheet — do nothing
    }

    // Fallback for browsers without file-sharing support: WhatsApp with text summary only
    window.open(
      `https://wa.me/?text=${encodeURIComponent(summaryText)}`,
      "_blank",
    );
  };

  const openEditPetty = (transaction) => {
    setEditingPetty(transaction);
    setPettyForm({
      type: transaction.type,
      itemName: transaction.itemName,
      amount: String(transaction.amount),
      paymentType: transaction.paymentType,
      customerName: transaction.customerName || "",
      customerMobile: transaction.customerMobile || "",
      remarks: transaction.remarks || "",
      qty: transaction.qty != null ? String(transaction.qty) : "",
      unit: transaction.unit || "",
      isPaid: transaction.isPaid !== false,
    });
    window.scrollTo({
      top: document.body.scrollHeight * 0.3,
      behavior: "smooth",
    });
  };

  const cancelEditPetty = () => {
    setEditingPetty(null);
    setPettyForm({
      type: "Sales",
      itemName: "",
      amount: "",
      paymentType: "Cash",
      customerName: "",
      customerMobile: "",
      remarks: "",
      qty: "",
      unit: "",
      isPaid: true,
    });
  };

  const handleDeletePetty = async (id) => {
    if (!isSubscribed) {
      showError('An active MiniERP EDI subscription is needed to delete entries.');
      return;
    }
    if (confirmDeleteId !== id) {
      // First tap only arms the confirm — no native confirm() dialog, matches the rest of the app's tone
      setConfirmDeleteId(id);
      setTimeout(
        () => setConfirmDeleteId((curr) => (curr === id ? null : curr)),
        4000,
      );
      return;
    }
    setConfirmDeleteId(null);
    try {
      await deletePettyTransaction(id);
      const res = await fetchPettyTransactions();
      setPettyTransactions(res?.data || []);
      showSuccess("Counter memo deleted.");
    } catch (err) {
      showError("Delete failed: " + err.message);
    }
  };

  const todayTransactions = pettyTransactions.filter((t) => {
    const d = new Date(t.date);
    const now = new Date();
    return d.toDateString() === now.toDateString();
  });
  const todayCount = todayTransactions.length;
  const todaySalesTotal = todayTransactions
    .filter((t) => t.type === "Sales")
    .reduce((s, t) => s + (t.amount || 0), 0);
  const todayPurchaseTotal = todayTransactions
    .filter((t) => t.type === "Purchase")
    .reduce((s, t) => s + (t.amount || 0), 0);

  // Memo sequence = position within THAT DAY's entries in chronological
  // (creation) order — computed across ALL loaded transactions, not just
  // today's, so History rows get the same stable memo number as Today's list.
  const seqById = useMemo(() => {
    const byDay = new Map();
    pettyTransactions.forEach((t) => {
      const dayKey = new Date(t.date).toDateString();
      if (!byDay.has(dayKey)) byDay.set(dayKey, []);
      byDay.get(dayKey).push(t);
    });
    const map = new Map();
    byDay.forEach((list) => {
      const sortedAsc = [...list].sort((a, b) => new Date(a.date) - new Date(b.date));
      sortedAsc.forEach((t, i) => map.set(t._id, i + 1));
    });
    return map;
  }, [pettyTransactions]);

  // ================= COUNTER HISTORY (last 30 days, filterable) =================
  const [historyFilters, setHistoryFilters] = useState({
    itemName: '',
    customerName: '',
    minAmount: '',
    maxAmount: '',
    paymentType: 'All',
    isPaid: 'All',
  });

  const historyTransactions = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);

    return pettyTransactions
      .filter((t) => new Date(t.date) >= cutoff)
      .filter((t) => !historyFilters.itemName || t.itemName?.toLowerCase().includes(historyFilters.itemName.toLowerCase()))
      .filter((t) => !historyFilters.customerName || t.customerName?.toLowerCase().includes(historyFilters.customerName.toLowerCase()))
      .filter((t) => !historyFilters.minAmount || t.amount >= Number(historyFilters.minAmount))
      .filter((t) => !historyFilters.maxAmount || t.amount <= Number(historyFilters.maxAmount))
      .filter((t) => historyFilters.paymentType === 'All' || t.paymentType === historyFilters.paymentType)
      .filter((t) => {
        if (historyFilters.isPaid === 'All') return true;
        const paid = t.isPaid !== false;
        return historyFilters.isPaid === 'Paid' ? paid : !paid;
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [pettyTransactions, historyFilters]);

  const historySalesTotal = historyTransactions.filter((t) => t.type === 'Sales').reduce((s, t) => s + (t.amount || 0), 0);
  const historyPurchaseTotal = historyTransactions.filter((t) => t.type === 'Purchase').reduce((s, t) => s + (t.amount || 0), 0);

  const resetHistoryFilters = () => setHistoryFilters({
    itemName: '', customerName: '', minAmount: '', maxAmount: '', paymentType: 'All', isPaid: 'All',
  });

  const hasActiveHistoryFilters = Object.entries(historyFilters).some(
    ([k, v]) => (k === 'paymentType' || k === 'isPaid') ? v !== 'All' : v !== ''
  );

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-row justify-between items-start md:items-center mb-4 border-b border-gray-700 pb-2">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-cyan-200">
              Think5Whys MiniERP
            </h1>
            <p className="text-gray-200 mt-1">Lightweight • Fast • Complete</p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm text-gray-200">Logged in as</p>
              <p className="text-xs text-gray-100">{userName}</p>
              <p className="text-xs text-gray-400 font-mono">{userCode}</p>
            </div>
            <div className="w-10 h-10 bg-cyan-600 rounded-full flex items-center justify-center text-xl">
              👤
            </div>
          </div>
        </div>

        {/* Seed Prompt */}
        {!loading && showSeedPrompt && (
          <div className="bg-gray-800 border border-cyan-500 rounded-xl p-6 mb-8">
            <h3 className="text-cyan-200 text-xl font-semibold mb-2">
              Welcome to MiniERP!
            </h3>
            <p className="text-gray-200 mb-4">
              No ledger groups found. Would you like to seed default Balance
              Sheet and P&amp;L groups?
            </p>
            <button
              onClick={handleSeedLedgers}
              className="bg-cyan-600 hover:bg-cyan-500 w-fit px-2 py-1 rounded-lg font-medium flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              Seed Default Ledgers
            </button>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-800 p-5 rounded-2xl">
            <div className="text-cyan-400 text-3xl mb-2">
              <FontAwesomeIcon icon={faFileAlt} />
            </div>
            <p className="text-2xl font-bold text-yellow-100">{ledgerCount}</p>
            <p className="text-gray-200 text-sm">Ledgers</p>
          </div>
          <div className="bg-gray-800 p-5 rounded-2xl">
            <div className="text-cyan-400 text-3xl mb-2">
              <FontAwesomeIcon icon={faWarehouse} />
            </div>
            <p className="text-2xl font-bold text-yellow-100">
              {inventoryCount}
            </p>
            <p className="text-gray-200 text-sm">Inventory Items</p>
          </div>
          <div className="bg-gray-800 p-5 rounded-2xl">
            <div className="text-cyan-400 text-3xl mb-2">
              <FontAwesomeIcon icon={faMoneyBill} />
            </div>
            <p className="text-2xl font-bold text-yellow-100">{todayCount}</p>
            <p className="text-gray-200 text-sm">Today's Counter Sales</p>
          </div>
          <div className="bg-gray-800 p-5 rounded-2xl">
            <div className="text-cyan-400 text-3xl mb-2">
              <FontAwesomeIcon icon={faChartBar} />
            </div>
            <a
              href="/minierp/reports"
              className="text-2xl font-bold text-yellow-100 hover:underline"
            >
              View
            </a>
            <p className="text-gray-200 text-sm">Reports &amp; Graphs</p>
          </div>
        </div>

        {/* Quick Counter Sales / Purchase */}
        <div className="bg-gray-800 rounded-3xl p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <h2 className="text-2xl font-semibold text-cyan-200 flex items-center gap-3">
              <FontAwesomeIcon icon={faMoneyBill} /> Quick Counter Memo
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              {isVoiceSupported && (
                <button
                  type="button"
                  onClick={() => (voice.armed ? voice.disarm() : voice.arm())}
                  className={`w-fit px-2 py-1 rounded-xl flex items-center gap-2 text-sm font-medium transition-colors ${
                    voice.armed
                      ? "bg-rose-600 hover:bg-rose-500"
                      : "bg-violet-600 hover:bg-violet-500"
                  }`}
                  title={
                    voice.armed
                      ? "Turn off voice assistant"
                      : "Turn on voice assistant"
                  }
                >
                  <FontAwesomeIcon
                    icon={voice.armed ? faMicrophoneSlash : faMicrophone}
                  />
                  {voice.armed ? "Voice On" : "🎤 Try Voice"}
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowTodayList((v) => !v)}
                className="bg-gray-700 hover:bg-gray-600 w-fit px-2 py-1 rounded-xl flex items-center gap-2 text-sm font-medium"
              >
                <FontAwesomeIcon icon={showTodayList ? faTimes : faListUl} />
                {showTodayList ? "Hide" : "Today Till Now"} ({todayCount})
              </button>
              <button
                type="button"
                onClick={() => setShowCounterHistory((v) => !v)}
                className="bg-gray-700 hover:bg-gray-600 w-fit px-2 py-1 rounded-xl flex items-center gap-2 text-sm font-medium"
              >
                <FontAwesomeIcon icon={showCounterHistory ? faTimes : faListUl} />
                {showCounterHistory ? "Hide History" : "History"}
              </button>
            </div>
          </div>

          {/* ================= VOICE STATUS PANEL ================= */}
          {voice.armed && (
            <div
              className={`rounded-2xl p-4 mb-5 border transition-colors ${
                voice.dictating
                  ? "bg-violet-950/40 border-violet-600"
                  : "bg-gray-900 border-gray-700"
              }`}
            >
              {voice.micError ? (
                <p className="text-rose-400 text-sm">🎙️ {voice.micError}</p>
              ) : !voice.micActive ? (
                <p className="text-amber-400 text-sm flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-pulse" />
                  ⏳ Starting microphone... (check the browser's mic permission icon in the address bar if this doesn't clear in a few seconds)
                </p>
              ) : !voice.dictating ? (
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-violet-300 text-sm flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-violet-400 animate-pulse" />
                    🎤 Listening... say{" "}
                    <span className="font-bold">"Thinky Start"</span> to fill
                    this memo hands-free
                  </p>
                  <button
                    type="button"
                    onClick={() => voice.setMuted((m) => !m)}
                    className="w-fit px-1.5 py-1.5 text-gray-400 hover:text-white"
                    title={
                      voice.muted
                        ? "Unmute voice prompts"
                        : "Mute voice prompts"
                    }
                  >
                    <FontAwesomeIcon
                      icon={voice.muted ? faVolumeMute : faVolumeUp}
                    />
                  </button>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <p className="text-white font-semibold text-sm flex items-center gap-2">
                      🗣️ Step {voice.stepIndex + 1} of {voice.totalFields}:{" "}
                      {voice.currentField?.label}
                    </p>
                    <button
                      type="button"
                      onClick={() => voice.setMuted((m) => !m)}
                      className="w-fit px-1.5 py-1.5 text-gray-400 hover:text-white"
                    >
                      <FontAwesomeIcon
                        icon={voice.muted ? faVolumeMute : faVolumeUp}
                      />
                    </button>
                  </div>
                  <p className="text-violet-300 text-sm">
                    {voice.currentField?.prompt}
                  </p>
                  {voice.lastHeard && (
                    <p className="text-gray-500 text-xs mt-1">
                      Heard: "{voice.lastHeard}"
                    </p>
                  )}
                  <p className="text-gray-500 text-xs mt-2">
                    Say <span className="font-semibold">"Thinky Save"</span>{" "}
                    anytime to submit, or{" "}
                    <span className="font-semibold">"Thinky Cancel"</span> to
                    stop.
                  </p>
                </div>
              )}
            </div>
          )}

          {showTodayList && (
            <div className="bg-gray-900 border border-gray-700 rounded-2xl p-4 mb-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="text-emerald-400 font-medium">
                    Sales: ₹{todaySalesTotal}
                  </span>
                  <span className="text-rose-400 font-medium">
                    Purchase: ₹{todayPurchaseTotal}
                  </span>
                </div>
                {isSubscribed && todayTransactions.length > 0 && (
                  <button
                    type="button"
                    onClick={() => generateRegisterCSV({
                      filename: 'todays-counter-memo',
                      columns: [
                        { header: 'Memo No', dataKey: 'memo' }, { header: 'Type', dataKey: 'type' },
                        { header: 'Item', dataKey: 'itemName' }, { header: 'Amount', dataKey: 'amountStr' },
                        { header: 'Payment', dataKey: 'paymentType' }, { header: 'Customer', dataKey: 'customerName' },
                      ],
                      rows: todayTransactions.map((t) => ({
                        memo: buildMemoNumber(t, seqById.get(t._id)), type: t.type, itemName: t.itemName,
                        amountStr: `Rs. ${t.amount}`, paymentType: t.paymentType, customerName: t.customerName || 'Walk-in',
                      })),
                    })}
                    className="w-fit px-2 py-1 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-xs flex items-center gap-1"
                  >
                    <FontAwesomeIcon icon={faFileExport} /> CSV
                  </button>
                )}
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {todayTransactions.length === 0 && (
                  <p className="text-gray-500 text-sm">
                    No counter entries yet today.
                  </p>
                )}
                {todayTransactions.map((t) => (
                  <TransactionRow key={t._id} t={t} />
                ))}
              </div>
            </div>
          )}

          {showCounterHistory && (
            <div className="bg-gray-900 border border-gray-700 rounded-2xl p-4 mb-5">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h3 className="text-sm font-semibold text-gray-300">📜 Last 30 Days</h3>
                {hasActiveHistoryFilters && (
                  <button type="button" onClick={resetHistoryFilters} className="text-xs text-cyan-400 hover:underline w-fit px-1 py-1">
                    Clear filters
                  </button>
                )}
              </div>

              {/* ================= FILTER BAR ================= */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
                <input
                  type="text"
                  value={historyFilters.itemName}
                  onChange={(e) => setHistoryFilters({ ...historyFilters, itemName: e.target.value })}
                  placeholder="Item name"
                  className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-xs text-gray-100"
                />
                <input
                  type="text"
                  value={historyFilters.customerName}
                  onChange={(e) => setHistoryFilters({ ...historyFilters, customerName: e.target.value })}
                  placeholder="Customer name"
                  className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-xs text-gray-100"
                />
                <input
                  type="number"
                  value={historyFilters.minAmount}
                  onChange={(e) => setHistoryFilters({ ...historyFilters, minAmount: e.target.value })}
                  placeholder="Min ₹"
                  className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-xs text-gray-100"
                />
                <input
                  type="number"
                  value={historyFilters.maxAmount}
                  onChange={(e) => setHistoryFilters({ ...historyFilters, maxAmount: e.target.value })}
                  placeholder="Max ₹"
                  className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-xs text-gray-100"
                />
                <select
                  value={historyFilters.paymentType}
                  onChange={(e) => setHistoryFilters({ ...historyFilters, paymentType: e.target.value })}
                  className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-xs text-gray-100"
                >
                  <option value="All">All Payment Modes</option>
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="Card">Card</option>
                  <option value="Credit">Credit</option>
                  <option value="Free">Free / Sample</option>
                </select>
                <select
                  value={historyFilters.isPaid}
                  onChange={(e) => setHistoryFilters({ ...historyFilters, isPaid: e.target.value })}
                  className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-xs text-gray-100"
                >
                  <option value="All">All Payment Status</option>
                  <option value="Paid">Paid</option>
                  <option value="Unpaid">Unpaid</option>
                </select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="text-emerald-400 font-medium">Sales: ₹{historySalesTotal}</span>
                  <span className="text-rose-400 font-medium">Purchase: ₹{historyPurchaseTotal}</span>
                  <span className="text-gray-500">{historyTransactions.length} entr{historyTransactions.length === 1 ? 'y' : 'ies'}</span>
                </div>
                {isSubscribed && historyTransactions.length > 0 && (
                  <button
                    type="button"
                    onClick={() => generateRegisterCSV({
                      filename: 'counter-memo-history',
                      columns: [
                        { header: 'Date', dataKey: 'dateStr' }, { header: 'Memo No', dataKey: 'memo' }, { header: 'Type', dataKey: 'type' },
                        { header: 'Item', dataKey: 'itemName' }, { header: 'Amount', dataKey: 'amountStr' },
                        { header: 'Payment', dataKey: 'paymentType' }, { header: 'Customer', dataKey: 'customerName' },
                      ],
                      rows: historyTransactions.map((t) => ({
                        dateStr: new Date(t.date).toLocaleDateString(), memo: buildMemoNumber(t, seqById.get(t._id)),
                        type: t.type, itemName: t.itemName, amountStr: `Rs. ${t.amount}`,
                        paymentType: t.paymentType, customerName: t.customerName || 'Walk-in',
                      })),
                    })}
                    className="w-fit px-2 py-1 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-xs flex items-center gap-1"
                  >
                    <FontAwesomeIcon icon={faFileExport} /> CSV
                  </button>
                )}
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {historyTransactions.length === 0 && (
                  <p className="text-gray-500 text-sm">
                    {hasActiveHistoryFilters ? 'No entries match these filters.' : 'No counter entries in the last 30 days.'}
                  </p>
                )}
                {historyTransactions.map((t) => (
                  <TransactionRow key={t._id} t={t} />
                ))}
              </div>
            </div>
          )}

          <StatusBanner status={status} className="mb-4" />

          <form
            onSubmit={handlePettySubmit}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            <div>
              <label className="text-gray-200 text-sm block mb-1">Customer or Business?</label>
              <select
                value={counterpartyKind}
                onChange={(e) => {
                  const kind = e.target.value;
                  setCounterpartyKind(kind);
                  if (kind === "Customer" && pettyForm.type === "Barter") {
                    setPettyForm((f) => ({ ...f, type: "Sales" }));
                  }
                }}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-gray-100"
              >
                <option value="Customer">👤 Customer (walk-in)</option>
                <option value="Business">🏢 Business (on MiniERP)</option>
              </select>
            </div>

            <div>
              <label className="text-gray-200 text-sm block mb-1">Type</label>
              <select
                value={pettyForm.type}
                onChange={(e) =>
                  setPettyForm({ ...pettyForm, type: e.target.value })
                }
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-gray-100"
              >
                <option value="Sales">Sales</option>
                <option value="Purchase">Purchase</option>
                {counterpartyKind === "Business" && <option value="Barter">🔄 Barter</option>}
              </select>
            </div>

            {pettyForm.type === "Barter" ? (
              <>
                <div>
                  <label className="text-gray-200 text-sm block mb-1">You Give</label>
                  <input
                    type="text"
                    value={businessForm.itemGiven}
                    onChange={(e) => setBusinessForm({ ...businessForm, itemGiven: e.target.value })}
                    placeholder="What you're giving"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-100"
                    required
                  />
                </div>
                <div>
                  <label className="text-gray-200 text-sm block mb-1">You Receive</label>
                  <input
                    type="text"
                    value={businessForm.itemReceived}
                    onChange={(e) => setBusinessForm({ ...businessForm, itemReceived: e.target.value })}
                    placeholder="What you're receiving"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-100"
                    required
                  />
                </div>
                <div>
                  <label className="text-gray-200 text-sm block mb-1">
                    Estimated Value (₹) <span className="text-gray-500">optional, record only</span>
                  </label>
                  <input
                    type="number"
                    value={pettyForm.amount}
                    onChange={(e) => setPettyForm({ ...pettyForm, amount: e.target.value })}
                    placeholder="0.00"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-100"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="text-gray-200 text-sm block mb-1">
                    Item Name
                  </label>
                  <input
                    type="text"
                    value={pettyForm.itemName}
                    onChange={(e) =>
                      setPettyForm({ ...pettyForm, itemName: e.target.value })
                    }
                    placeholder="Item name"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-100"
                    required
                  />
                </div>

                <div>
                  <label className="text-gray-200 text-sm block mb-1">
                    Amount (₹)
                  </label>
                  <input
                    type="number"
                    value={pettyForm.amount}
                    onChange={(e) =>
                      setPettyForm({ ...pettyForm, amount: e.target.value })
                    }
                    placeholder="0.00"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-100"
                    required
                  />
                </div>

                <div>
                  <label className="text-gray-200 text-sm block mb-1">
                    Qty <span className="text-gray-500">(optional)</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={pettyForm.qty}
                    onChange={(e) =>
                      setPettyForm({ ...pettyForm, qty: e.target.value })
                    }
                    placeholder="e.g. 3"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-100"
                  />
                </div>

                {pettyForm.qty && (
                  <div>
                    <label className="text-gray-200 text-sm block mb-1">Unit</label>
                    <input
                      type="text"
                      value={pettyForm.unit}
                      onChange={(e) =>
                        setPettyForm({ ...pettyForm, unit: e.target.value })
                      }
                      placeholder="e.g. Pcs, Kg, Box"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-100"
                    />
                  </div>
                )}
              </>
            )}

            {counterpartyKind === "Customer" && (
              <div>
                <label className="text-gray-200 text-sm block mb-1">
                  Payment Mode
                </label>
                <select
                  value={pettyForm.paymentType}
                  onChange={(e) =>
                    setPettyForm({ ...pettyForm, paymentType: e.target.value })
                  }
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-gray-100"
                >
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="Card">Card</option>
                  <option value="Credit">Credit</option>
                  <option value="Free">Free / Sample</option>
                </select>
              </div>
            )}

            <div>
              <label className="text-gray-200 text-sm block mb-1">
                Payment Status <span className="text-gray-500">(optional)</span>
              </label>
              <select
                value={pettyForm.isPaid ? "Paid" : "Unpaid"}
                onChange={(e) =>
                  setPettyForm({
                    ...pettyForm,
                    isPaid: e.target.value === "Paid",
                  })
                }
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-gray-100"
              >
                <option value="Paid">Paid</option>
                <option value="Unpaid">Unpaid</option>
              </select>
            </div>

            {counterpartyKind === "Business" ? (
              <div>
                <label className="text-gray-200 text-sm flex items-center justify-between mb-1">
                  Business Code
                  <MiniERPContactPicker
                    contactType="Business"
                    currentCode={businessForm.counterpartyCode}
                    onPick={(c) => setBusinessForm({ ...businessForm, counterpartyCode: c.businessUserCode })}
                  />
                </label>
                <input
                  type="text"
                  value={businessForm.counterpartyCode}
                  onChange={(e) => setBusinessForm({ ...businessForm, counterpartyCode: e.target.value.toUpperCase().slice(0, 14) })}
                  placeholder="14-character MiniERP code"
                  maxLength={14}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-100 font-mono"
                  required
                />
              </div>
            ) : (
              <div>
                <label className="text-gray-200 text-sm flex items-center justify-between mb-1">
                  Customer / Supplier
                  <MiniERPContactPicker
                    contactType="Customer"
                    currentName={pettyForm.customerName}
                    currentMobile={pettyForm.customerMobile}
                    onPick={(c) => setPettyForm({ ...pettyForm, customerName: c.name, customerMobile: c.mobile })}
                  />
                </label>
                <input
                  type="text"
                  value={pettyForm.customerName}
                  onChange={(e) =>
                    setPettyForm({ ...pettyForm, customerName: e.target.value })
                  }
                  placeholder="Optional"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-100"
                />
              </div>
            )}

            {counterpartyKind === "Customer" && (
              <div>
                <label className="text-gray-200 text-sm block mb-1">
                  Mobile Number <span className="text-gray-500">(optional)</span>
                </label>
                <input
                  type="tel"
                  value={pettyForm.customerMobile}
                  onChange={(e) =>
                    setPettyForm({
                      ...pettyForm,
                      customerMobile: e.target.value.replace(/[^\d+ ]/g, ""),
                    })
                  }
                  placeholder="e.g. 9876543210"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-100"
                />
              </div>
            )}

            <div className="md:col-span-2 lg:col-span-3">
              <label className="text-gray-200 text-sm block mb-1">
                Remarks <span className="text-gray-500">(optional)</span>
              </label>
              <textarea
                value={pettyForm.remarks}
                onChange={(e) =>
                  setPettyForm({ ...pettyForm, remarks: e.target.value })
                }
                style={{ height: "100px" }}
                placeholder="Optional notes for this memo..."
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-100"
              />
            </div>

            <div className="md:col-span-2 lg:col-span-3 flex items-end">
              <button
                type="submit"
                className="bg-emerald-600 hover:bg-emerald-500 w-fit px-2 py-1  rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} /> Record {pettyForm.type}
              </button>
            </div>
          </form>
        </div>

        {/* Navigation Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {[
            {
              title: "Accounting Period",
              icon: faCalendarCheck,
              path: "/minierp/periods",
              color: "cyan",
            },
            {
              title: "Ledger Master",
              icon: faFileAlt,
              path: "/minierp/ledgers",
              color: "cyan",
            },
            {
              title: "Inventory",
              icon: faWarehouse,
              path: "/minierp/inventory",
              color: "emerald",
            },
            {
              title: "Voucher Entry",
              icon: faMoneyBill,
              path: "/minierp/vouchers",
              color: "amber",
            },
            {
              title: "Stock In/Out",
              icon: faWarehouse,
              path: "/minierp/stock",
              color: "rose",
            },
            {
              title: "Authorised Users",
              icon: faUsers,
              path: "/minierp/users",
              color: "violet",
            },
            {
              title: "Attendance",
              icon: faCalendarCheck,
              path: "/minierp/attendance",
              color: "sky",
            },
            {
              title: "Reports",
              icon: faChartBar,
              path: "/minierp/reports",
              color: "sky",
            },
            {
              title: "Export Center",
              icon: faFileExport,
              path: "/minierp/export",
              color: "indigo",
            },
            {
              title: "Local EDI",
              icon: faFileInvoice,
              path: "/minierp/edi",
              color: "indigo",
            },
            {
              title: "SalesAccelerator",
              icon: faFileInvoice,
              path: "/minierp/saleskit",
              color: "cyan",
            },
             {
              title: "PriceFlow",
              icon: faFileInvoice,
              path: "/minierp/priceflow",
              color: "sky",
            },
             {
              title: "Offline",
              icon: faBattleNet,
              path: "/minierp/offline",
              color: "emerald",
            },
            {
              title: "TraS",
              icon: faUserClock,
              path: "/minierp/tras",
              color: "rose",
            },
             {
              title: "AI",
              icon: faBrain,
              path: "",
              // path: "/minierp/ai",
              color: "zinc",
            },
          ].map((item, index) => (
            <a
              key={index}
              href={item.path}
              className="bg-gray-800 hover:bg-gray-700 transition-all px-4 py-2 rounded-xl group border border-gray-700 hover:border-cyan-500/30"
            >
              <div
                className={`text-2xl lg:text-4xl mb-2 ${ICON_COLORS[item.color] || ICON_COLORS.cyan} group-hover:scale-105 transition-transform`}
              >
                <FontAwesomeIcon icon={item.icon} />
              </div>
              <h3 className="text-lg lg:text-xl font-semibold text-gray-50">
                {item.title}
              </h3>
              <p className="text-gray-300 mt-1 text-xs lg:text-sm">
                Manage • View • Edit
              </p>
            </a>
          ))}
        </div>

        <div className="mt-4 text-center text-gray-300 text-sm">
          Think5Whys MiniERP v1.0 • Built for speed and simplicity
        </div>
      </div>
    </div>
  );
};

export default MiniERPHome;
